import * as React from 'react';
import { Route, Routes, useNavigate, useLocation, } from "react-router-dom-v5-compat";
import queryString from 'query-string';

import LockedPage from './locked_page'
import Sidebar from './sidebar'
import SmartGroups from './smart_groups';
import PerformanceBreakdown from './performance_breakdown';
import UpgradeModal from './upgrade_modal'
import AlreadyAssignedTargetedPracticeModal from './already_assigned_targeted_practice_modal';
import AssignTargetedPracticeModal from './assign_targeted_practice_modal';
import SkillsReportHeader from './skills_report_header'
import SkillsReportSubheader from './skills_report_subheader';
import { ClassroomInterface, SkillCategoryInterface, StudentPerformanceData, SkillPerformanceData, SkillPracticeRecommendation, SkillPracticeAssignment } from './interfaces'
import { SKILLS_REPORT_PRACTICE_SCOPE, SKILLS_REPORT_USE_ELL_SKILLS, SKILLS_REPORT_MOMENTUM_SCORE_GOAL, } from './shared'

import { ReportPremiumProvider } from '../shared/report_premium_context';
import MomentumScoreGoalModal from '../shared/momentum_score_goal_modal';
import { NO_CLASSROOM, ALL_PRACTICE, PREMIUM_STATE_PAID, PREMIUM_STATE_LOCKED, PREMIUM_STATE_TRIAL, PREMIUM_STATE_NONE, CLASS_PRACTICE, smartGroupIllustrationSrc, } from '../shared/shared'
import VerificationBanner from '../verification/verification_banner';
import { SMALL_ICON_BASE_SRC, getPageTitle, Snackbar, defaultSnackbarTimeout, MobileSidebarButtonContainer, pluralize, TEACHER, ADMIN, APPROVED, HIGHER_ED_OR_INTERNATIONAL_SCHOOL, } from '../../../../Shared/index'
import useHideFooter from '../../../../Shared/hooks/useHideFooter'
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor'
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../progress_report_constants';
import { requestGet, } from '../../../../../modules/request'

const openSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-open.svg`
const closeSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-close.svg`

interface SkillsReportProps {
  adminVerificationStatus: string;
  allSkillCategories: SkillCategoryInterface[];
  allSkillSubCategories: SkillCategoryInterface[];
  alternativeSchoolNames: string[];
  classrooms: ClassroomInterface[];
  needsEmailVerification: boolean;
  premiumState: typeof PREMIUM_STATE_PAID | typeof PREMIUM_STATE_TRIAL | typeof PREMIUM_STATE_LOCKED | typeof PREMIUM_STATE_NONE;
  role: string;
  school: any;
  schoolAdmins: any[];
  teacherVerificationStatus: string;
  hasSchoolOrDistrictPremium: boolean;
}

const SkillsReport = ({
  adminVerificationStatus,
  allSkillCategories,
  allSkillSubCategories,
  alternativeSchoolNames,
  classrooms,
  needsEmailVerification,
  premiumState,
  role,
  school,
  schoolAdmins,
  teacherVerificationStatus,
  hasSchoolOrDistrictPremium,
}: SkillsReportProps) => {
  document.title = getPageTitle("View Reports", "Skills Report")

  const defaultIncludeELLValue = window.localStorage.getItem(SKILLS_REPORT_USE_ELL_SKILLS) === 'true'
  const [includeELL, setIncludeELL] = React.useState<boolean>(defaultIncludeELLValue);
  const [filteredSkillCategories, setFilteredSkillCategories] = React.useState([])
  const [filteredSkillSubCategories, setFilteredSkillSubCategories] = React.useState([])
  const [classroom, setClassroom] = React.useState(null)
  const [showSidebar, setShowSidebar] = React.useState(true)
  const [showMobileSidebar, setShowMobileSidebar] = React.useState(false)
  const [practiceScope, setPracticeScope] = React.useState<string>(window.localStorage.getItem(SKILLS_REPORT_PRACTICE_SCOPE) || CLASS_PRACTICE);
  const [showMomentumScoreGoalModal, setShowMomentumScoreGoalModal] = React.useState(false)
  const [momentumScoreGoal, setMomentumScoreGoal] = React.useState(window.localStorage.getItem(SKILLS_REPORT_MOMENTUM_SCORE_GOAL) ? Number(window.localStorage.getItem(SKILLS_REPORT_MOMENTUM_SCORE_GOAL)) : 80)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')

  // Assign targeted practice modal state
  const [showAlreadyAssignedTargetedPracticeModal, setShowAlreadyAssignedTargetedPracticeModal] = React.useState(false)
  const [showAssignTargetedPracticeModal, setShowAssignTargetedPracticeModal] = React.useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false)
  const [assignmentSkillId, setAssignmentSkillId] = React.useState<number | null>(null)
  const [assignmentStudentIds, setAssignmentStudentIds] = React.useState<number[]>([])
  const [assignmentSmartGroup, setAssignmentSmartGroup] = React.useState<string| null>(null)

  const [manuallySetSelectedSkillCategoryId, setManuallySetSelectedSkillCategoryId] = React.useState<boolean>(false)

  const [selectedSkillCategoryId, setSelectedSkillCategoryId] = React.useState<any>(null);
  const [selectedSkillSubCategoryId, setSelectedSkillSubCategoryId] = React.useState<any>(null);
  const [selectedStudentId, setSelectedStudentId] = React.useState<any>(null);

  // Data state for student and skill performance
  const [studentData, setStudentData] = React.useState<{[key: string]: StudentPerformanceData[]}>({})
  const [skillData, setSkillData] = React.useState<{[key: string]: SkillPerformanceData[]}>({})
  const [studentDataKey, setStudentDataKey] = React.useState<string>('')
  const [skillDataKey, setSkillDataKey] = React.useState<string>('')

  // Test data state for skill practice recommendations and assignments
  const [skillPracticeRecommendations, setSkillPracticeRecommendations] = React.useState<SkillPracticeRecommendation[]>([])
  const [skillPracticeAssignments, setSkillPracticeAssignments] = React.useState<SkillPracticeAssignment[]>([])

  const navigate = useNavigate();
  const location = useLocation();

  const prevPathname = React.useRef(location.pathname);

  const isByStudent = location.pathname.includes('by_student');
  const isBySkill = location.pathname.includes('by_skill');

  useHideFooter()
  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const showLockedPage = ![PREMIUM_STATE_PAID, PREMIUM_STATE_TRIAL].includes(premiumState)
  const userNeedsVerification = needsEmailVerification || (role === TEACHER && teacherVerificationStatus !== APPROVED) || (role === ADMIN && adminVerificationStatus && adminVerificationStatus !== APPROVED)

  // Check for skill_category_id query parameter on initial load (used when navigating from Standards Report skill tags)
  const initialSkillCategoryIdFromUrl = React.useMemo(() => {
    const params = queryString.parse(location.search);
    if (params.skill_category_id) {
      const skillCategoryId = Number(params.skill_category_id);
      if (!isNaN(skillCategoryId)) {
        return skillCategoryId;
      }
    }
    return null;
  }, []);

  React.useEffect(() => {
    const localStorageSelectedClassroomId = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID)
    const classroomFromLocalStorageClassroomId = classrooms.find(c => Number(c.id) === Number(localStorageSelectedClassroomId))
    setClassroom(classroomFromLocalStorageClassroomId || classrooms[0] || NO_CLASSROOM)
  }, [])

  React.useEffect(() => {
    const newFilteredSkillCategories = allSkillCategories.filter(skillCategory => includeELL ? skillCategory.ell : skillCategory.gen_ed)
    const newFilteredSkillSubCategories = allSkillSubCategories.filter(skillSubCategory => includeELL ? skillSubCategory.ell : skillSubCategory.gen_ed)
    setFilteredSkillCategories(newFilteredSkillCategories)
    setFilteredSkillSubCategories(newFilteredSkillSubCategories)

    if (selectedSkillCategoryId && !newFilteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId)) {
      setSelectedSkillCategoryId(isByStudent ? newFilteredSkillCategories[0].id : null)
      setManuallySetSelectedSkillCategoryId(false)
    }

    if (selectedSkillSubCategoryId && !newFilteredSkillSubCategories.find(skillSubCategory => skillSubCategory.id === selectedSkillSubCategoryId)) {
      setSelectedSkillSubCategoryId(null)
    }
  }, [includeELL])

  React.useEffect(() => {
    if (!classroom || showLockedPage) { return }

    requestGet('/skills_reports/practice_recommendations', (body) => {
      setSkillPracticeRecommendations(body)
    })

    fetchPracticeAssignmentsData()
  }, [classroom])

  React.useEffect(() => {
    if (!classroom) { return }
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, classroom.id)
  }, [classroom])

  React.useEffect(() => {
    window.localStorage.setItem(SKILLS_REPORT_PRACTICE_SCOPE, practiceScope)
  }, [practiceScope])

  React.useEffect(() => {
    window.localStorage.setItem(SKILLS_REPORT_USE_ELL_SKILLS, includeELL.toString())
  }, [includeELL])

  React.useEffect(() => {
    window.localStorage.setItem(SKILLS_REPORT_MOMENTUM_SCORE_GOAL, momentumScoreGoal)
  }, [momentumScoreGoal])

  // Handle filter state management based on current tab type
  React.useEffect(() => {
    if (isByStudent) {
      if (!selectedSkillCategoryId && filteredSkillCategories.length > 0) {
        setSelectedSkillCategoryId(filteredSkillCategories[0].id);
      }

      if (selectedStudentId !== null) {
        setSelectedStudentId(null);
      }
    } else if (isBySkill) {
      if (selectedSkillSubCategoryId !== null) {
        setSelectedSkillSubCategoryId(null);
      }

      // Apply skill_category_id from URL if present and valid
      if (initialSkillCategoryIdFromUrl && filteredSkillCategories.length > 0) {
        const categoryExistsInFiltered = filteredSkillCategories.find(cat => cat.id === initialSkillCategoryIdFromUrl);
        if (categoryExistsInFiltered && !manuallySetSelectedSkillCategoryId) {
          setSelectedSkillCategoryId(initialSkillCategoryIdFromUrl);
          setManuallySetSelectedSkillCategoryId(true);
        }
      } else if (!manuallySetSelectedSkillCategoryId) {
        setSelectedSkillCategoryId(null)
      }
    }

    prevPathname.current = location.pathname;
  }, [location.pathname, filteredSkillCategories, initialSkillCategoryIdFromUrl]);

  React.useEffect(() => {
    if (!selectedStudentId) { return }

    setSelectedStudentId(null)
  }, [classroom])

  React.useEffect(() => {
    if (location.pathname === '/teachers/progress_reports/skills_report' && classroom?.id) {
      // Preserve query string when redirecting (e.g., skill_category_id from Standards Report)
      navigate(`/teachers/progress_reports/skills_report/classroom/${classroom?.id}/smart_groups/by_skill${location.search}`);
    }
  }, [location.pathname, navigate, classroom?.id]);

  // Fetch student performance data
  React.useEffect(() => {
    if (!classroom?.id || showLockedPage) return;

    const selectedSkillCategory = filteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId);
    const selectedSkillSubCategory = filteredSkillSubCategories.find(skillSubCategory => skillSubCategory.id === selectedSkillSubCategoryId);

    const queryHash = {
      concept_id: selectedSkillCategory?.id,
      sub_concept_id: selectedSkillSubCategory?.id,
      classroom_id: classroom.id,
      all_history: practiceScope === ALL_PRACTICE,
      include_ell: includeELL
    };

    const newStudentDataKey = JSON.stringify(queryHash);
    setStudentDataKey(newStudentDataKey);

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: '/skills_reports/performance_breakdown_by_student',
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body: { data: StudentPerformanceData[] }) => {
      setStudentData(prevData => ({...prevData, [newStudentDataKey]: body.data}));
    });
  }, [classroom?.id, selectedSkillCategoryId, selectedSkillSubCategoryId, practiceScope, includeELL, filteredSkillCategories, filteredSkillSubCategories]);

  // Fetch skill performance data
  React.useEffect(() => {
    if (!classroom?.id || showLockedPage) return;

    const selectedSkillCategory = filteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId);

    const queryHash = {
      concept_id: selectedSkillCategory?.id,
      classroom_id: classroom.id,
      all_history: practiceScope === ALL_PRACTICE,
      include_ell: includeELL,
      user_id: selectedStudentId,
    };

    const newSkillDataKey = JSON.stringify(queryHash);
    setSkillDataKey(newSkillDataKey);

    const url = queryString.stringifyUrl(
      {
        query: queryHash,
        url: '/skills_reports/performance_breakdown_by_skill',
      },
      { arrayFormat: 'bracket' }
    );

    requestGet(url, (body: { data: SkillPerformanceData[] }) => {
      setSkillData(prevData => ({...prevData, [newSkillDataKey]: body.data}));
    });
  }, [classroom?.id, selectedSkillCategoryId, practiceScope, selectedStudentId, includeELL, filteredSkillCategories]);

  function fetchPracticeAssignmentsData() {
    requestGet('/skills_reports/practice_assignments', (body) => {
      setSkillPracticeAssignments(body)
    })
  }

  function handleCloseMomentumScoreGoalModal(snackbarMessage?: string) {
    setShowMomentumScoreGoalModal(false)
    if (typeof snackbarMessage === 'string') {
      setSnackbarText(snackbarMessage)
      setShowSnackbar(true)
    }
  }

  function handleCloseUpgradeModal() {
    setShowUpgradeModal(false)
  }

  function handleCloseAlreadyAssignedTargetedPracticeModal() {
    setShowAlreadyAssignedTargetedPracticeModal(false)
  }

  function handleCloseAssignTargetedPracticeModal(snackbarMessage?: string) {
    setShowAssignTargetedPracticeModal(false)
    fetchPracticeAssignmentsData()
    if (typeof snackbarMessage === 'string') {
      setSnackbarText(snackbarMessage)
      setShowSnackbar(true)
    }
  }

  function openMobileSidebar() { setShowMobileSidebar(true) }

  function closeMobileSidebar() { setShowMobileSidebar(false) }

  function toggleSidebar() { setShowSidebar(!showSidebar) }

  function switchToClassPractice() {
    setPracticeScope(CLASS_PRACTICE)
  }

  function setSkillCategoryById(categoryId: number | null) {
    setManuallySetSelectedSkillCategoryId(!!categoryId)
    setSelectedSkillCategoryId(categoryId);
    setSelectedSkillSubCategoryId(null);
  }

  function handleSetSelectedSkillCategoryId(skillCategory: any) {
    setSkillCategoryById(skillCategory.value);
  }

  function handleSetSelectedSkillSubCategoryId(skillSubCategory: any) {
    setSelectedSkillSubCategoryId(skillSubCategory.value);
  }

  function handleSetSelectedStudentId(student: any) {
    setSelectedStudentId(student.value);
  }

  function handleSkillCategoryClick(skillCategoryId: number) {
    setSkillCategoryById(skillCategoryId);
  }

  function handleStudentClick(studentId: number | string) {
    setSelectedStudentId(Number(studentId));
    // Navigate to by_skill view when a student is clicked
    const currentPath = location.pathname;
    navigate(currentPath.replace('/by_student', '/by_skill'));
  }

  function handleAssignTargetedPracticeClick(skillId: number, studentIds: number[], smartGroup?: string) {
    if (!hasSchoolOrDistrictPremium) {
      setShowUpgradeModal(true)
      return
    }

    const filteredSkillPracticeRecommendations = skillPracticeRecommendations.filter(rec => rec.concept_id === skillId);

    if (filteredSkillPracticeRecommendations.length === 0) {
      const skillIsSubCategory = !!allSkillSubCategories.find(skillSubCategory => skillSubCategory.id === skillId)
      setSnackbarText(`Sorry, there is no targeted practice available for this ${skillIsSubCategory ? 'skill sub-category' : 'skill category'}`)
      setShowSnackbar(true)
      return
    }

    if (filteredSkillPracticeRecommendations.every(recommendation => getStudentAssignmentData(recommendation.unit_template_id, skillId, studentIds).newAssignmentStudentIds.length === 0)) {
      if (studentIds.length === 1) {
        const studentName = classroom.students.find(student => student.id === studentIds[0]).name
        const activityPackText = filteredSkillPracticeRecommendations.length === 1 ? filteredSkillPracticeRecommendations[0].unit_template_name : filteredSkillPracticeRecommendations.map(rec => rec.unit_template_name).join(' and the ')
        setSnackbarText(`${studentName} has already been assigned the ${activityPackText} ${pluralize(filteredSkillPracticeRecommendations.length, 'activity pack', 'activity packs')}`)
        setShowSnackbar(true)
      } else {
        setAssignmentSkillId(skillId)

        setShowAlreadyAssignedTargetedPracticeModal(true)
      }
    } else {
      setAssignmentSkillId(skillId)
      setAssignmentStudentIds(studentIds)
      setAssignmentSmartGroup(smartGroup)

      setShowAssignTargetedPracticeModal(true)
    }
  }

  function getStudentAssignmentData(selectedPackId, skillIdForAssignment=assignmentSkillId, studentIdsForAssignment=assignmentStudentIds) {
    const filteredSkillPracticeRecommendations = skillPracticeRecommendations.filter(rec => rec.concept_id === skillIdForAssignment);
    const filteredSkillPracticeAssignments = skillPracticeAssignments.filter(assignment =>
      assignment.classroom_id === classroom?.id &&
      filteredSkillPracticeRecommendations.some(rec => rec.unit_template_id === assignment.unit_template_id)
    );

    if (!classroom.students) {
      return {
        alreadyAssignedStudentIds: [],
        newAssignmentStudentIds: studentIdsForAssignment,
        alreadyAssignedStudents: [],
        newAssignmentStudents: [],
        allSelectedStudents: []
      };
    }

    // Get students who already have this assignment
    const alreadyAssignedStudentIds = filteredSkillPracticeAssignments
      .filter(assignment =>
        assignment.unit_template_id === selectedPackId &&
        assignment.student_ids.some(studentId => studentIdsForAssignment.includes(studentId))
      )
      .reduce((acc, assignment) => {
        const overlappingStudents = assignment.student_ids.filter(studentId => studentIdsForAssignment.includes(studentId));
        return [...acc, ...overlappingStudents];
      }, [] as number[]);

    // Filter out already assigned students to get only new assignments
    const newAssignmentStudentIds = studentIdsForAssignment.filter(studentId =>
      !alreadyAssignedStudentIds.includes(studentId)
    );

    // Get full student objects
    const alreadyAssignedStudents = classroom.students.filter(student =>
      alreadyAssignedStudentIds.includes(student.id)
    );
    const newAssignmentStudents = classroom.students.filter(student =>
      newAssignmentStudentIds.includes(student.id)
    );
    const allSelectedStudents = classroom.students.filter(student =>
      studentIdsForAssignment.includes(student.id)
    );

    return {
      alreadyAssignedStudentIds,
      newAssignmentStudentIds,
      alreadyAssignedStudents,
      newAssignmentStudents,
      allSelectedStudents
    };
  }

  function renderShowSidebarButton() {
    const ariaLabel = showSidebar ? 'Close sidebar' : 'Open sidebar'

    const imgSrc = showSidebar ? closeSidebarIconSrc : openSidebarIconSrc

    return (
      <button
        aria-expanded={showSidebar}
        aria-label={ariaLabel}
        className='interactive-wrapper focus-on-light show-sidebar-button'
        onClick={toggleSidebar}
        type="button"
      >
        <img alt="" src={imgSrc} />
      </button>
    )
  }

  let sidebar: React.ReactElement | null = null

  if (showSidebar || showMobileSidebar) {
    sidebar = (
      <Sidebar
        classrooms={classrooms}
        closeMobileSidebar={closeMobileSidebar}
        selectedClassroomId={classroom?.id}
        setSelectedClassroom={setClassroom}
        showMobileSidebar={showMobileSidebar}
        showSidebarButton={renderShowSidebarButton()}
      />
    )
  }

  function renderNeedsVerificationEmptyState() {
    return (
      <section className="empty-state">
        <h2>Get verified to see your students’ full practice history</h2>
        <p>After completing the steps above to get verified, you will unlock each student’s full practice history across all teachers and classes for a comprehensive view of their progress and growth. In the meantime, you can access Class Practice.</p>
        <button className="quill-button green contained medium focus-on-light" onClick={switchToClassPractice} type="button">Switch to Class Practice</button>
      </section>
    )
  }

  function buildAdminContactMailto() {
    const subject = "Can you help unlock Quill's full Skills Report?";

    let toEmail = "";

    if (schoolAdmins?.length === 1) {
      toEmail = schoolAdmins[0].email || "";
    }

    const body = `Hi there,

I've been using Quill's new Skills Report, and it's already giving me really helpful insights into my students' progress. With a School or District Premium plan, the report can do even more, like showing each student's full learning history and letting me assign targeted practice directly from the report.

Those tools would make it much easier to turn data into action and better support each student's growth. You can see the full list of Premium features here: https://www.quill.org/premium

There's more about the Skills Report in this article: https://support.quill.org/en/articles/9343360-how-do-i-read-the-skills-report-quill-premium

If you'd like to learn more or explore pricing options, you can fill out this short form to connect with Quill's Partnerships Team: https://quillorg.salesmate.io/webforms/#/5d6a5b51-8ed9-49d5-923e-dcab1baa0063

Thanks for considering it!`;

    return `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function renderUpgradeEmptyState() {
    return (
      <section className="empty-state upgrade-empty-state">
        <img alt="" src={smartGroupIllustrationSrc} />
        <h2>See the Full Story of Each Student’s Journey</h2>
        <p>Go beyond the work in just your class. The All Practice view unlocks a student’s complete learning history, including practice from previous years and other teachers, to give you a true, day-one baseline of their skills.</p>
        <p><b>Upgrading to a School or District Premium plan unlocks the full power of this report.</b> You’ll gain the ability to see the complete, year-over-year learning history for every student. You’ll also unlock the power to turn those insights into immediate action and assign targeted practice with a single click.</p>
        <div className="buttons-wrapper">
          <a className="quill-button green outlined medium focus-on-light" href='/premium' rel="noopener noreferrer" target="_blank">Learn more about School & District Premium</a>
          <a className="quill-button green contained medium focus-on-light" href={buildAdminContactMailto()}>Contact your admin</a>
        </div>
      </section>
    )
  }

  function renderHeader(title: string) {
    return (
      <React.Fragment>
        <SkillsReportHeader
          byStudent={location.pathname.includes('by_student')}
          selectedSkillCategory={filteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId)}
          setShowMomentumScoreGoalModal={setShowMomentumScoreGoalModal}
          title={title}
        />
        <MobileSidebarButtonContainer openMobileSidebar={openMobileSidebar} />
        <SkillsReportSubheader
          byStudent={location.pathname.includes('by_student')}
          classroom={classroom}
          filteredSkillCategories={filteredSkillCategories}
          filteredSkillSubCategories={filteredSkillSubCategories}
          includeELL={includeELL}
          onSkillCategoryChange={handleSetSelectedSkillCategoryId}
          onSkillSubCategoryChange={handleSetSelectedSkillSubCategoryId}
          onStudentChange={handleSetSelectedStudentId}
          practiceScope={practiceScope}
          selectedSkillCategoryId={selectedSkillCategoryId}
          selectedSkillSubCategoryId={selectedSkillSubCategoryId}
          selectedStudentId={selectedStudentId}
          setIncludeELL={setIncludeELL}
          setPracticeScope={setPracticeScope}
        />
      </React.Fragment>
    );
  }

  function renderVerificationBanner() {
    const isHigherEdOrInternationSchoolAndAllPractice = school && HIGHER_ED_OR_INTERNATIONAL_SCHOOL.includes(school.name) && practiceScope === ALL_PRACTICE

    const shouldShowVerificationBanner =
      isHigherEdOrInternationSchoolAndAllPractice ||
      (userNeedsVerification &&
        practiceScope !== CLASS_PRACTICE &&
        hasSchoolOrDistrictPremium)

    if (!shouldShowVerificationBanner) return null

    return (
      <VerificationBanner
        adminVerificationStatus={adminVerificationStatus}
        alternativeSchoolNames={alternativeSchoolNames}
        needsEmailVerification={needsEmailVerification}
        role={role}
        school={school}
        schoolAdmins={schoolAdmins}
        teacherVerificationStatus={teacherVerificationStatus}
      />
    )
  }

  if (showLockedPage) {
    return (
      <LockedPage
        premiumState={premiumState}
      />
    )
  }


  const sharedProps = {
    filteredSkillCategories,
    filteredSkillSubCategories,
    classroom,
    includeELL,
    momentumScoreGoal,
    onSkillCategoryClick: handleSkillCategoryClick,
    onStudentChange: handleSetSelectedStudentId,
    onStudentClick: handleStudentClick,
    handleAssignTargetedPracticeClick,
    practiceScope,
    renderHeader,
    renderNeedsVerificationEmptyState,
    renderVerificationBanner,
    renderUpgradeEmptyState,
    selectedSkillCategoryId,
    selectedSkillSubCategoryId,
    selectedStudentId,
    skillData,
    skillDataKey,
    studentData,
    studentDataKey,
    userNeedsVerification,
  }

  const filteredSkillPracticeRecommendations = skillPracticeRecommendations.filter(rec => rec.concept_id === assignmentSkillId);

  return (
    <ReportPremiumProvider hasSchoolOrDistrictPremium={hasSchoolOrDistrictPremium}>
      <div className="skills-report-container">
        {showUpgradeModal && (
          <UpgradeModal
            buildAdminContactMailto={buildAdminContactMailto}
            closeModal={handleCloseUpgradeModal}
          />
        )}
        {showAlreadyAssignedTargetedPracticeModal && (
          <AlreadyAssignedTargetedPracticeModal
            closeModal={handleCloseAlreadyAssignedTargetedPracticeModal}
            skillPracticeRecommendations={filteredSkillPracticeRecommendations}
          />
        )}
        {showAssignTargetedPracticeModal && (
          <AssignTargetedPracticeModal
            assignmentSmartGroup={assignmentSmartGroup}
            assignmentStudentIds={assignmentStudentIds}
            classroom={classroom}
            closeModal={handleCloseAssignTargetedPracticeModal}
            getStudentAssignmentData={getStudentAssignmentData}
            skillPracticeRecommendations={filteredSkillPracticeRecommendations}
          />
        )}
        {showMomentumScoreGoalModal && (
          <MomentumScoreGoalModal
            closeModal={handleCloseMomentumScoreGoalModal}
            momentumScoreGoal={momentumScoreGoal}
            setMomentumScoreGoal={setMomentumScoreGoal}
          />
        )}
        {sidebar}
        <div className={showSidebar ? 'sidebar-open' : 'sidebar-closed'}>
          {showSidebar ? null : renderShowSidebarButton()}
          <Routes>
            <Route
              element={<SmartGroups {...sharedProps} skillPracticeRecommendations={skillPracticeRecommendations} />}
              path='/classroom/:classroomId/smart_groups/*'
            />
            <Route
              element={<PerformanceBreakdown {...sharedProps} />}
              path='/classroom/:classroomId/performance_breakdown/*'
            />
          </Routes>
        </div>
        <Snackbar text={snackbarText} visible={showSnackbar} />
      </div>
    </ReportPremiumProvider>
  )
}

export default SkillsReport
